import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { isAnalyticsSection } from 'components/DisasterGPT/constants/opordSectionDefaults';
import SitrepSectionEditDrawer from 'components/DisasterGPT/SitrepSectionEditDrawer';
import { createSectionTemplate } from 'components/DisasterGPT/hooks/useSectionTemplates';
import {
  useRefreshSitrepV2,
  useAddSitrepSectionV2,
  useMoveSitrepSectionV2,
  useSitrepSectionAILocks,
  type SitrepSectionType,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import ReportsFileAttachments from 'components/Reports/ReportsFileAttachments';
import ReportsSectionDisplay from 'components/Reports/ReportsSectionDisplay';
import { AnalyticsLayerActionsProvider } from 'components/Reports/document/AnalyticsLayerActions';
import SitrepDocumentView from 'components/Reports/document/SitrepDocumentView';
import {
  ReportContentSearch,
  sectionMatchesReportSearch,
} from 'components/Reports/ReportContentSearch';
import { useSitrepDraftPresence } from 'components/Reports/collab/useSitrepDraftPresence';
import { SitrepPresenceAvatars } from 'components/Reports/collab/SitrepPresenceAvatars';
import {
  aiEnabledSectionIdsUnder,
  nestSitrepSections,
} from 'components/Reports/document/nestSitrepSections';
import { ReportPresentation } from 'components/Reports/presentation/ReportPresentation';
import SitrepSectionTabs from 'components/Reports/SitrepSectionTabs';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import DraftSitrepActionsMenu from 'components/Reports/DraftSitrepActionsMenu';
import SitrepCardActionsMenu from 'components/Reports/SitrepCardActionsMenu';
import ReviewCardActions from 'components/Reports/ReviewCardActions';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import { useSectionAutoRefresh } from 'components/Reports/hooks/useSectionAutoRefresh';
import { useSitrepLayoutMode } from 'components/Reports/hooks/useSitrepLayoutMode';
import useOpenReportSelection from 'components/Reports/hooks/useOpenReportSelection';
import useOpenSectionSettings from 'components/Reports/hooks/useOpenSectionSettings';
import useSitrepSectionActions from 'components/Reports/hooks/useSitrepSectionActions';
import { registerReportsTourApi } from 'components/Reports/reportsTour/reportsTourBridge';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';
import {
  SitrepCardSurface,
  type EditableReportsSitrepCardProps,
} from 'components/Reports/reports.types';
import { Settings, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Sentry from '../../instrument';
import { useAppSelector, selectGroupGuid } from 'slices/commonSelectors';
import {
  useReportVersioningFlag,
  useSitrepCollabFlag,
} from 'utils/featureFlags';
import {
  SitrepDynamicDataProvider,
  SitrepSubmittedDataTabs,
} from 'components/Reports/section/SitrepDynamicDataContext';
import { sectionRendersSnapshotToggle } from 'components/Reports/section/dynamicSectionSnapshot';

/**
 * Editable SITREP card used on Draft and Review surfaces.
 *
 * ### Draft surface
 *
 * Full editing is always on: add/remove/reorder sections, AI generate, section
 * settings, file attachments; add section / add files / delete draft / submit
 * for review via the header kebab. Categories are named folders; sections hold
 * content. There is intentionally no SITREP-wide Save button — each section is
 * saved individually.
 *
 * ### Review surface
 *
 * Approve / reject is the **primary** workflow (always visible). An explicit
 * **"Edit before approving"** toggle lets reviewers make changes before
 * approval when needed.
 */
const ReportsSitrepCard: React.FC<EditableReportsSitrepCardProps> = ({
  sitrep,
  surface,
  headerBanner,
}) => {
  const tabSetId = React.useId();
  const isDraft = surface === SitrepCardSurface.Draft;
  const isReview = surface === SitrepCardSurface.Review;
  const isPendingReviewDraft = isDraft && sitrep.approval_status === 'PENDING';
  const [isReviewEditMode, setIsReviewEditMode] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  // Reports tour: the draft it just created is the `openReport` match, and only
  // that card lends its kebab to the walkthrough.
  const [draftMenuOpen, setDraftMenuOpen] = useState(false);
  const [presentation, setPresentation] = useState<'off' | 'document' | 'quad'>(
    'off'
  );
  const { isOpenReportMatch } = useOpenReportSelection(
    sitrep.id,
    sitrep.item_id
  );
  const isTourDraft = (isDraft || isReview) && isOpenReportMatch;
  useEffect(() => {
    if (!isTourDraft) return undefined;
    return registerReportsTourApi({
      openDraftKebab: () => setDraftMenuOpen(true),
      closeDraftKebab: () => setDraftMenuOpen(false),
    });
  }, [isTourDraft]);
  const sitrepCollabEnabled = useSitrepCollabFlag();
  const reportVersioning = useReportVersioningFlag();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, sectionsLoading, refetch, refreshedAt } = cardData;
  const { layoutMode, setLayoutMode } = useSitrepLayoutMode(sitrep, sitrepData);
  // The card stays open. One in-progress draft per area or incident keeps
  // presence to a single connection for the open scope.
  const presenceSitrepId =
    sitrepCollabEnabled && (isDraft || isReviewEditMode) ? sitrep.id : null;
  const { viewers: draftViewers } = useSitrepDraftPresence(presenceSitrepId);
  const nestedSections = useMemo(
    () => nestSitrepSections(sitrepData?.sections ?? []),
    [sitrepData?.sections]
  );
  const topLevelSections = useMemo(
    () => nestedSections.map(({ children: _c, ...section }) => section),
    [nestedSections]
  );
  const [reportQuery, setReportQuery] = useState('');
  const visibleTopLevel = useMemo(
    () =>
      topLevelSections.filter((section) =>
        sectionMatchesReportSearch(
          section,
          reportQuery,
          nestedSections.find((item) => item.id === section.id)?.children ?? []
        )
      ),
    [nestedSections, reportQuery, topLevelSections]
  );
  const { activeSectionIndex, activeSection, setActiveSectionIndex } =
    useSitrepSectionSelector(visibleTopLevel);
  const activeChildSections = useMemo(() => {
    if (!activeSection) return [];
    return (
      nestedSections.find((s) => s.id === activeSection.id)?.children ?? []
    );
  }, [activeSection, nestedSections]);

  const {
    editSettingsSection,
    setEditSettingsSection,
    handleSaveSettings,
    handleDeleteSection,
    handleMoveSection,
    canMoveUp,
    canMoveDown,
  } = useSitrepSectionActions(sitrep.id, sitrepData?.sections, refetch);
  const { matchedSection, setSectionId } = useOpenSectionSettings(
    sitrepData?.sections
  );

  const openSettings = (section: SitrepSectionType | null) => {
    if (section && isAnalyticsSection(section)) return;
    setEditSettingsSection(section);
    setSectionId(section?.id ?? null);
  };

  useEffect(() => {
    if (
      matchedSection &&
      !isAnalyticsSection(matchedSection) &&
      editSettingsSection?.id !== matchedSection.id
    ) {
      setEditSettingsSection(matchedSection);
    }
  }, [editSettingsSection?.id, matchedSection, setEditSettingsSection]);

  const groupGuid = useAppSelector(selectGroupGuid);
  const addSectionMutation = useAddSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();
  const moveSectionMutation = useMoveSitrepSectionV2();

  const canUseDraftAuthoringControls = isDraft && !isPendingReviewDraft;
  const canEditContent = canUseDraftAuthoringControls || isReviewEditMode;
  const reportApprovalStatus =
    sitrep.approval_status ?? (isReview ? 'PENDING' : sitrep.approval_status);
  const sitrepWithApproval = {
    ...sitrep,
    approval_status: reportApprovalStatus,
  };

  const hasAISection = (sitrepData?.sections ?? []).some(
    (s) => s.type === 'ai' || !!s.parent_section_id
  );
  const activeHasAiGenerate =
    activeSection?.type === 'ai' || activeChildSections.length > 0;
  const shouldPollAILocks =
    canUseDraftAuthoringControls &&
    (layoutMode === 'document' ? hasAISection : activeHasAiGenerate);
  const { data: sectionAILocks } = useSitrepSectionAILocks(
    shouldPollAILocks ? sitrep.id : undefined
  );
  const isActiveSectionLocked = activeSection
    ? !!sectionAILocks?.get(activeSection.id)
    : false;
  const lockedSectionIds = useMemo(
    () => new Set(sectionAILocks ? [...sectionAILocks.keys()] : []),
    [sectionAILocks]
  );
  useSectionAutoRefresh({
    sitrepId: sitrep.id,
    sections: sitrepData?.sections,
    lockedSectionIds,
    enabled: true,
  });

  const handleAddBlankSection = async () => {
    try {
      const template = await createSectionTemplate(
        {
          name: 'New Section',
          type: 'ai',
          output_format_structured: { summary_options: [] },
        },
        groupGuid
      );
      await addSectionMutation.mutateAsync({
        sitrepId: sitrep.id,
        sectionTemplateId: template.id as UUID,
        selectedDatetime: new Date().toISOString(),
        sitrep_source: sitrep.sitrep_source,
        sitrep_source_id: sitrep.sitrep_source_id,
        order: (sitrepData?.sections?.length ?? 0) + 1,
        aor_id: sitrep.aor_id ?? '',
      });
      const result = await refetch();
      const updatedSections = result.data?.sections;
      if (updatedSections && updatedSections.length > 0) {
        openSettings(updatedSections[updatedSections.length - 1]);
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to add section');
    }
  };

  const handleAIGenerateDraft = async () => {
    if (!activeSection) return;
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: [activeSection.id],
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleAIGenerateSection = async (section: SitrepSectionType) => {
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: [section.id],
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const aiSectionIds = (sitrepData?.sections ?? [])
    .filter((section) => section.type === 'ai')
    .map((section) => section.id);
  const handleAIGenerateAll = async () => {
    if (aiSectionIds.length === 0) return;
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: aiSectionIds,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };
  const handleAIGenerateIds = async (sectionIds: string[]) => {
    if (sectionIds.length === 0) return;
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: sectionIds,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };
  const showGenerateAll = canEditContent && aiSectionIds.length > 0;
  const activeCategoryAiIds = activeSection
    ? aiEnabledSectionIdsUnder(sitrepData?.sections ?? [], activeSection.id)
    : [];

  const handleReorderSection = (
    section: SitrepSectionType,
    targetOrder: number
  ) => {
    moveSectionMutation.mutate({
      sitrepId: sitrep.id,
      sectionId: section.id,
      targetOrder,
    });
  };

  const reviewEditButton = isReview ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-pressed={isReviewEditMode}
      onClick={() => {
        if (isReviewEditMode) {
          openSettings(null);
        }
        setIsReviewEditMode((v) => !v);
      }}
    >
      {isReviewEditMode ? 'Done editing' : 'Edit before Approval'}
    </Button>
  ) : null;

  return (
    <SitrepCardShell
      sitrep={sitrep}
      surface={surface}
      cardData={cardData}
      headerBanner={headerBanner}
      headerRight={
        <div
          className="tw:flex tw:items-center tw:gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {showGenerateAll ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={refreshSitrepMutation.isPending}
              onClick={() => {
                void handleAIGenerateAll();
              }}
            >
              <Sparkles
                className={
                  refreshSitrepMutation.isPending
                    ? 'tw:animate-spin'
                    : undefined
                }
              />
              AI Generate All
            </Button>
          ) : null}
          {isDraft ? (
            <DraftSitrepActionsMenu
              sitrep={sitrep}
              layoutMode={layoutMode}
              onLayoutModeChange={setLayoutMode}
              showSubmitForReview={!isPendingReviewDraft}
              onAddSection={
                canUseDraftAuthoringControls ? handleAddBlankSection : undefined
              }
              onAddFiles={
                canUseDraftAuthoringControls
                  ? () => setFilePickerOpen(true)
                  : undefined
              }
              open={draftMenuOpen}
              onOpenChange={setDraftMenuOpen}
              presentDisabled={sectionsLoading}
              onPresent={() => {
                setDraftMenuOpen(false);
                setPresentation('document');
              }}
              onQuadChart={() => {
                setDraftMenuOpen(false);
                setPresentation('quad');
              }}
            />
          ) : (
            <SitrepCardActionsMenu
              sitrep={sitrep}
              layoutMode={layoutMode}
              onLayoutModeChange={setLayoutMode}
              presentDisabled={sectionsLoading}
              onPresent={() => setPresentation('document')}
              onQuadChart={() => setPresentation('quad')}
            />
          )}
        </div>
      }
      headerTrailing={reviewEditButton}
    >
      <AnalyticsLayerActionsProvider
        sitrep={sitrep}
        sections={sitrepData?.sections}
        enabled={canUseDraftAuthoringControls}
      >
        <SitrepDynamicDataProvider
          approvalStatus={reportApprovalStatus}
          sections={sitrepData?.sections}
        >
          {presentation !== 'off' ? (
            <ReportPresentation
              sitrep={sitrepWithApproval}
              sections={sitrepData?.sections ?? []}
              canEdit={canEditContent}
              showAuthoringActions={canUseDraftAuthoringControls}
              sectionAILocks={sectionAILocks}
              isGenerating={refreshSitrepMutation.isPending}
              layout={presentation}
              onRefetch={() => refetch()}
              onOpenSettings={openSettings}
              onDeleteSection={handleDeleteSection}
              onAIGenerate={handleAIGenerateSection}
              onExit={() => setPresentation('off')}
            />
          ) : null}
          {((refreshedAt && !reportVersioning) || sitrepCollabEnabled) && (
            <div className="tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              {refreshedAt && !reportVersioning && (
                <Alert className="tw:min-w-0 tw:flex-1 tw:border-success tw:bg-success/10 tw:px-3 tw:py-2">
                  <AlertDescription className="tw:text-xs tw:font-normal tw:text-emerald-200">
                    Last updated: {refreshedAt} by{' '}
                    {sitrep.updated_by_user_name ??
                      sitrep.created_by_user_name ??
                      'Unknown'}
                  </AlertDescription>
                </Alert>
              )}
              {sitrepCollabEnabled && (
                <SitrepPresenceAvatars viewers={draftViewers} />
              )}
            </div>
          )}

          {layoutMode === 'document' ? (
            <SitrepDocumentView
              sitrep={sitrepWithApproval}
              sections={sitrepData?.sections ?? []}
              canEdit={canEditContent}
              showAuthoringActions={canUseDraftAuthoringControls}
              sectionAILocks={sectionAILocks}
              isGenerating={refreshSitrepMutation.isPending}
              onRefetch={() => refetch()}
              onOpenSettings={openSettings}
              onDeleteSection={handleDeleteSection}
              onAIGenerate={handleAIGenerateSection}
              onAIGenerateMany={
                canEditContent
                  ? (sectionIds) => {
                      void handleAIGenerateIds(sectionIds);
                    }
                  : undefined
              }
              onReorderSection={
                canUseDraftAuthoringControls ? handleReorderSection : undefined
              }
              footer={
                <div className="mt-3">
                  <ReportsFileAttachments
                    sitrepId={sitrep.id}
                    workspaceId={sitrep.incident_id}
                    readOnly={!canUseDraftAuthoringControls}
                    pickerOpen={
                      canUseDraftAuthoringControls ? filePickerOpen : undefined
                    }
                    onPickerOpenChange={
                      canUseDraftAuthoringControls
                        ? setFilePickerOpen
                        : undefined
                    }
                    hideAddButton={canUseDraftAuthoringControls}
                  />
                </div>
              }
            />
          ) : (
            <>
              {topLevelSections.length > 0 ? (
                <ReportContentSearch
                  value={reportQuery}
                  onChange={setReportQuery}
                />
              ) : null}
              {reportQuery.trim() && visibleTopLevel.length === 0 ? (
                <p className="tw:mb-3 tw:text-sm tw:text-muted-foreground">
                  No sections match that search.
                </p>
              ) : null}
              {visibleTopLevel.length > 0 && (
                <SitrepSectionTabs
                  sections={visibleTopLevel}
                  activeSectionIndex={activeSectionIndex}
                  onSectionChange={setActiveSectionIndex}
                  className="mb-3"
                  idBase={tabSetId}
                />
              )}

              {canUseDraftAuthoringControls &&
                activeSection &&
                (activeHasAiGenerate || !isAnalyticsSection(activeSection)) && (
                  <div className="tw:mb-3 tw:flex tw:items-center tw:gap-2">
                    {activeCategoryAiIds.length > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={refreshSitrepMutation.isPending}
                        onClick={() => {
                          void handleAIGenerateIds(activeCategoryAiIds);
                        }}
                      >
                        <Sparkles
                          className={
                            refreshSitrepMutation.isPending
                              ? 'tw:animate-spin'
                              : undefined
                          }
                        />
                        AI Generate All
                      </Button>
                    ) : null}
                    {activeHasAiGenerate && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  data-tour="reports-ai-generate"
                                  disabled={
                                    refreshSitrepMutation.isPending ||
                                    isActiveSectionLocked
                                  }
                                  onClick={() => {
                                    void handleAIGenerateDraft();
                                  }}
                                  style={
                                    isActiveSectionLocked
                                      ? { pointerEvents: 'none' }
                                      : undefined
                                  }
                                >
                                  <Sparkles
                                    className={
                                      refreshSitrepMutation.isPending
                                        ? 'tw:animate-spin'
                                        : undefined
                                    }
                                  />
                                  {refreshSitrepMutation.isPending
                                    ? 'Generating...'
                                    : 'AI Generate Draft'}
                                </Button>
                              </span>
                            }
                          />
                          {isActiveSectionLocked ? (
                            <TooltipContent className="dark">
                              Someone else is editing this section right now.
                              Wait until they&apos;re finished and you can AI
                              generate a draft.
                            </TooltipContent>
                          ) : null}
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {!isAnalyticsSection(activeSection) && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => openSettings(activeSection)}
                      >
                        <Settings />
                        Section Settings
                      </Button>
                    )}
                  </div>
                )}

              {isReview &&
                isReviewEditMode &&
                activeSection &&
                !isAnalyticsSection(activeSection) && (
                  <div className="tw:mb-3 tw:flex tw:items-center tw:gap-2">
                    {activeCategoryAiIds.length > 0 ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={refreshSitrepMutation.isPending}
                        onClick={() => {
                          void handleAIGenerateIds(activeCategoryAiIds);
                        }}
                      >
                        <Sparkles
                          className={
                            refreshSitrepMutation.isPending
                              ? 'tw:animate-spin'
                              : undefined
                          }
                        />
                        AI Generate All
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openSettings(activeSection)}
                    >
                      <Settings />
                      Section Settings
                    </Button>
                  </div>
                )}

              {activeSection && (
                <div
                  id={`${tabSetId}-panel-${activeSectionIndex}`}
                  role="tabpanel"
                  aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
                  className="reports-card__section-content"
                >
                  {sectionRendersSnapshotToggle(
                    activeSection,
                    activeChildSections
                  ) ? (
                    <SitrepSubmittedDataTabs />
                  ) : null}
                  <ReportsSectionDisplay
                    key={activeSection.id}
                    section={activeSection}
                    sitrepId={sitrep.id}
                    sitrep={sitrepWithApproval}
                    sitrepWorkspaceId={sitrep.incident_id}
                    sitrepTemplateId={sitrep.sitrep_template_id}
                    childSections={activeChildSections}
                    onUpdated={() => refetch()}
                    canEdit={canEditContent}
                    onAIGenerate={handleAIGenerateSection}
                  />
                </div>
              )}
              <div className="mt-3">
                <ReportsFileAttachments
                  sitrepId={sitrep.id}
                  workspaceId={sitrep.incident_id}
                  readOnly={!canUseDraftAuthoringControls}
                  pickerOpen={
                    canUseDraftAuthoringControls ? filePickerOpen : undefined
                  }
                  onPickerOpenChange={
                    canUseDraftAuthoringControls ? setFilePickerOpen : undefined
                  }
                  hideAddButton={canUseDraftAuthoringControls}
                />
              </div>
            </>
          )}

          {isReview && (
            <ReviewCardActions sitrep={sitrep} isEditMode={isReviewEditMode} />
          )}

          {editSettingsSection && (
            <SitrepSectionEditDrawer
              onHide={() => openSettings(null)}
              layoutMode={layoutMode}
              onSave={async (payload) => {
                // Drop the `section` URL param first; handleSaveSettings clears the
                // drawer state and the URL->drawer sync effect would otherwise
                // reopen it for the still-present param.
                setSectionId(null);
                await handleSaveSettings(payload);
              }}
              onDelete={() => handleDeleteSection(editSettingsSection)}
              onMoveUp={() => handleMoveSection('up')}
              onMoveDown={() => handleMoveSection('down')}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              section={editSettingsSection}
              sitrepId={sitrep.id}
              sitrepTemplateId={sitrep.sitrep_template_id}
              reportSections={sitrepData?.sections}
              parentDataSources={
                editSettingsSection.parent_section_id
                  ? (
                      sitrepData?.sections?.find(
                        (s) => s.id === editSettingsSection.parent_section_id
                      ) as { data_sources?: unknown } | undefined
                    )?.data_sources
                  : undefined
              }
            />
          )}
        </SitrepDynamicDataProvider>
      </AnalyticsLayerActionsProvider>
    </SitrepCardShell>
  );
};

export default ReportsSitrepCard;
